// scroll3d.js — RacketUp scroll-driven 3D padel scene
// Designs are BAKED INTO the racket's UV texture: they live on the actual surface
// (following curvature, carbon weave and scene lighting) instead of floating planes.
// Scroll journey: clean product colours first, then stripes / padel club / company / player designs.
// Loads a Draco GLB when present (assets/racket.glb), else the FBX.
import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const canvas   = document.getElementById('scene-canvas');
const loaderEl = document.getElementById('boot');
const barEl    = document.getElementById('boot-bar');
const actEl    = document.getElementById('act');

// Pause the render loop while the 3D act is off-screen, so the page doesn't
// keep rendering at 60fps (and taxing the fixed header's backdrop-filter) for
// the whole scroll journey below it. Same pattern as assets/racketspec.js.
let sceneVisible = true;
if(actEl){
  new IntersectionObserver(es=>{ sceneVisible = es[0].isIntersecting; }, {threshold:0}).observe(actEl);
}

const clamp = (v,a,b)=>Math.max(a,Math.min(b,v));
const lerp  = (a,b,t)=>a+(b-a)*t;
function key(p, stops){
  if(p<=stops[0][0]) return stops[0][1];
  if(p>=stops[stops.length-1][0]) return stops[stops.length-1][1];
  for(let i=0;i<stops.length-1;i++){ const [a,va]=stops[i],[b,vb]=stops[i+1];
    if(p>=a&&p<=b){ const t=(p-a)/(b-a); const e=t*t*(3-2*t); return lerp(va,vb,e); } }
  return stops[stops.length-1][1];
}
const NIGHT='#10180A', POWDER='#FBFCF5', CHART='#DBFF01';
function lum(hex){ hex=hex.replace('#',''); const r=parseInt(hex.slice(0,2),16),g=parseInt(hex.slice(2,4),16),b=parseInt(hex.slice(4,6),16); return (0.299*r+0.587*g+0.114*b)/255; }
function hx(c){return parseInt(c,16);}
function hexLerp(a,b,t){ a=a.replace('#','');b=b.replace('#','');
  const r=Math.round(lerp(hx(a.slice(0,2)),hx(b.slice(0,2)),t));
  const g=Math.round(lerp(hx(a.slice(2,4)),hx(b.slice(2,4)),t));
  const bl=Math.round(lerp(hx(a.slice(4,6)),hx(b.slice(4,6)),t));
  return '#'+[r,g,bl].map(v=>v.toString(16).padStart(2,'0')).join(''); }

// RacketUp logomark: three skewed slashes
function drawLogo(ctx, x, y, h, col){
  const sw=h*0.17, gap=h*0.11, skew=h*0.30;
  ctx.fillStyle=col;
  for(let i=0;i<3;i++){ const sx=x+i*(sw+gap);
    ctx.beginPath(); ctx.moveTo(sx+skew,y); ctx.lineTo(sx+skew+sw,y); ctx.lineTo(sx+sw,y+h); ctx.lineTo(sx,y+h); ctx.closePath(); ctx.fill(); }
}
const LOGO_W = h => 3*(h*0.17)+2*(h*0.11)+h*0.30;

// ---- UV atlas of the racket face texture (measured from the model's UVs) ----
const TEXS = 2048; // atlas resolution
const ATLAS = {
  front:{ x:0.148*TEXS, y:(1-0.811)*TEXS, w:(0.364-0.148)*TEXS, h:(0.811-0.454)*TEXS },
  back: { x:0.539*TEXS, y:(1-0.811)*TEXS, w:(0.754-0.539)*TEXS, h:(0.811-0.454)*TEXS },
};

// ---- artwork helpers ----
function arcText(x, cx, cy, rad, txt, px, col, spread){
  x.save(); x.fillStyle=col; x.textAlign='center'; x.textBaseline='middle';
  x.font='800 '+px+'px Lato, sans-serif';
  const n=txt.length;
  for(let i=0;i<n;i++){ const t=n===1?0.5:i/(n-1);
    const ang=-spread/2 + t*spread - Math.PI/2;
    const px2=cx+Math.cos(ang)*rad, py2=cy+Math.sin(ang)*rad;
    x.save(); x.translate(px2,py2); x.rotate(ang+Math.PI/2); x.fillText(txt[i],0,0); x.restore();
  }
  x.restore();
}
function monogram(x, cx, cy, px, a, b, col, alpha){
  x.save(); x.fillStyle=col; x.textBaseline='middle';
  x.font='900 '+px+'px Georgia, "Times New Roman", serif';
  x.textAlign='right'; x.fillText(a, cx+px*0.18, cy);
  if(alpha!=null) x.globalAlpha=alpha;
  x.textAlign='left';  x.fillText(b, cx-px*0.18, cy);
  x.restore();
}
function mountains(x, cx, cy, w, col, lw){
  x.save(); x.strokeStyle=col; x.lineWidth=lw; x.lineJoin='round';
  x.beginPath();
  x.moveTo(cx-w*0.5, cy);
  x.lineTo(cx-w*0.20, cy-w*0.30);
  x.lineTo(cx-w*0.05, cy-w*0.13);
  x.lineTo(cx+w*0.16, cy-w*0.40);
  x.lineTo(cx+w*0.5, cy);
  x.stroke();
  // snow caps
  x.beginPath(); x.moveTo(cx+0.06*w,cy-0.30*w); x.lineTo(cx+0.16*w,cy-0.40*w); x.lineTo(cx+0.26*w,cy-0.30*w); x.stroke();
  x.restore();
}
function jet(x, cx, cy, s, col){
  x.save(); x.translate(cx,cy); x.fillStyle=col;
  x.beginPath(); x.moveTo(0,-s); x.lineTo(s*0.11,-s*0.42); x.lineTo(s*0.11,s*0.55); x.lineTo(-s*0.11,s*0.55); x.lineTo(-s*0.11,-s*0.42); x.closePath(); x.fill();
  x.beginPath(); x.moveTo(s*0.10,-s*0.12); x.lineTo(s*0.78,s*0.36); x.lineTo(s*0.78,s*0.52); x.lineTo(s*0.10,s*0.24); x.closePath(); x.fill();
  x.beginPath(); x.moveTo(-s*0.10,-s*0.12); x.lineTo(-s*0.78,s*0.36); x.lineTo(-s*0.78,s*0.52); x.lineTo(-s*0.10,s*0.24); x.closePath(); x.fill();
  x.beginPath(); x.moveTo(s*0.11,s*0.42); x.lineTo(s*0.36,s*0.72); x.lineTo(s*0.13,s*0.56); x.closePath(); x.fill();
  x.beginPath(); x.moveTo(-s*0.11,s*0.42); x.lineTo(-s*0.36,s*0.72); x.lineTo(-s*0.13,s*0.56); x.closePath(); x.fill();
  x.fillStyle='#2a2c24'; x.beginPath(); x.arc(0,-s*0.55,s*0.05,0,7); x.fill();
  x.restore();
}
// teardrop silhouette of the racket face (point toward the handle, bottom)
const FACE={ x:48, y:96, w:624, h:980 };  // teardrop region inside the paint canvas (resized at load)
function facePath(x, R, inset){
  const m=inset||0; const cx=R.x+R.w/2, top=R.y+m, bot=R.y+R.h-m, lx=R.x+m, rx=R.x+R.w-m;
  x.beginPath();
  x.moveTo(cx, top);
  x.bezierCurveTo(lx-R.w*0.06, R.y+R.h*0.005, lx-R.w*0.06, R.y+R.h*0.74, cx, bot);
  x.bezierCurveTo(rx+R.w*0.06, R.y+R.h*0.74, rx+R.w*0.06, R.y+R.h*0.005, cx, top);
  x.closePath();
}

// ---- design library — recreated from real RacketUp products, baked onto the face UV ----
// ---- design library — REAL racket designs (transparent teardrop PNGs) mapped 1:1 onto the face ----
const DESIGNS = [
  { img:null,                          frame:'#2733d6', grip:POWDER, band:POWDER },  // 0 · clean premium hero (iets donkerder blauw)
  { img:'assets/design-nec.png',       frame:NIGHT,     grip:POWDER,   band:POWDER    },  // 1 · NEC Nijmegen × Peakz (contra: zwart frame, witte grip)
  { img:'assets/design-mvppup.png',    frame:'#7C1F2B', grip:POWDER,   band:POWDER    },  // 2 · MVP Pup (contra: wijnrood frame, witte grip)
  { img:'assets/design-campbellblair.png', frame:'#E4D5BC', grip:'#5C4433', band:'#5C4433' },  // 3 · Campbell Blair (beige frame, bruine grip)
  { img:'assets/design-artigiano.png', frame:'#C9A24A', grip:NIGHT,    band:NIGHT     },  // 4 · L'Artigiano (contra: goud frame, zwarte grip)
  { img:'assets/design-jeff.png',      frame:NIGHT,     grip:POWDER,  band:POWDER    },  // 5 · Jeff · Corinthians
];
// Resolve an asset path to a Shopify (or other host) URL when a map is provided.
// window.RACKETUP_ASSETS is keyed by basename, e.g. { 'design-nec.png': 'https://cdn.shopify.com/.../design-nec.png' }
const ASSET_MAP = (typeof window!=='undefined' && window.RACKETUP_ASSETS) || {};
function assetURL(p){ if(!p) return p; const base=p.split('/').pop().split('?')[0]; const rs=(typeof window!=='undefined' && window.__resources) || {}; return rs[base] || ASSET_MAP[base] || p; }
const DESIGN_IMGS = DESIGNS.map(d=>{ if(!d.img) return null; const im=new Image(); im.crossOrigin='anonymous'; im.src=assetURL(d.img); return im; });
const designIndexAt = p => clamp(Math.floor(p*DESIGNS.length), 0, DESIGNS.length-1);

// procedural carbon-fibre normal map (twill weave)
let _carbonNormal=null;
function carbonNormal(){
  if(_carbonNormal) return _carbonNormal;
  const S=256, c=document.createElement('canvas'); c.width=c.height=S; const x=c.getContext('2d');
  x.fillStyle='#8080ff'; x.fillRect(0,0,S,S);
  const cell=S/8;
  for(let gy=0; gy<8; gy++){ for(let gx=0; gx<8; gx++){
    const over=((gx+gy)%2===0);
    const x0=gx*cell, y0=gy*cell;
    const g = over ? x.createLinearGradient(x0,y0,x0+cell,y0+cell)
                   : x.createLinearGradient(x0+cell,y0,x0,y0+cell);
    g.addColorStop(0,'#6f6fff'); g.addColorStop(0.5,'#9a9aff'); g.addColorStop(1,'#6f6fff');
    x.fillStyle=g; x.fillRect(x0,y0,cell,cell);
  }}
  const t=new THREE.CanvasTexture(c); t.wrapS=t.wrapT=THREE.RepeatWrapping; t.repeat.set(7,9); t.anisotropy=4;
  _carbonNormal=t; return t;
}

if (canvas) {
  try { boot(); }
  catch(err){ console.warn('3D unavailable:', err && err.message); sceneFallback(); }
}

function sceneFallback(){
  // WebGL unavailable — dismiss loader, mark page, keep content usable
  if(loaderEl) loaderEl.classList.add('done');
  document.documentElement.classList.add('scene-fail');
  window.boostScene = { setProgress(){}, setName(){}, designCount:0, ready:false };
}

function boot(){
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias:true, alpha:true, powerPreference:'high-performance' });
  } catch(e){
    // retry once after a moment (context-loss block can be transient), else fall back
    setTimeout(()=>{ try{ boot2(); }catch(e2){ sceneFallback(); } }, 1800);
    function boot2(){ renderer = new THREE.WebGLRenderer({ canvas, antialias:true, alpha:true }); bootWith(renderer); }
    return;
  }
  bootWith(renderer);
}

function bootWith(renderer){
  renderer.setPixelRatio(Math.min(devicePixelRatio,2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.06;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.03).texture;

  const camera = new THREE.PerspectiveCamera(32, 1, 1, 4000);
  camera.position.set(0, 8, 580);

  const key1 = new THREE.DirectionalLight(0xffffff, 2.0);
  key1.position.set(220,360,420); key1.castShadow=true;
  key1.shadow.mapSize.set(1024,1024); key1.shadow.bias=-0.0004;
  key1.shadow.camera.near=120; key1.shadow.camera.far=1600;
  key1.shadow.camera.left=-360;key1.shadow.camera.right=360;key1.shadow.camera.top=360;key1.shadow.camera.bottom=-360;
  scene.add(key1);
  const chartL = new THREE.DirectionalLight(0xdbff01, 1.5); chartL.position.set(-300,140,-120); scene.add(chartL);
  const fill = new THREE.DirectionalLight(0xffffff, 0.7); fill.position.set(260,-160,220); scene.add(fill);
  scene.add(new THREE.HemisphereLight(0xbfe34a, 0x05060a, 0.45));

  const ground = new THREE.Mesh(new THREE.PlaneGeometry(2200,2200), new THREE.ShadowMaterial({opacity:0.34}));
  ground.rotation.x=-Math.PI/2; ground.position.y=-235; ground.receiveShadow=true; scene.add(ground);

  const world = new THREE.Group(); scene.add(world);
  const racketRot = new THREE.Group();
  const racketPos = new THREE.Group();
  racketPos.add(racketRot); world.add(racketPos);
  const ball = buildBall(); world.add(ball);

  // ---- design state ----
  const state = { name:'JOUW NAAM' };
  const frameMats=[], gripMats=[], bandMats=[];
  let faceTex=null, fctx=null, faceMat=null;
  let prevIdx=0, curIdx=0, fadeT=1;
  let ready=false, targetP=0;

  function buildBall(){
    const c=document.createElement('canvas'); c.width=1024; c.height=512; const x=c.getContext('2d');
    const grad=x.createLinearGradient(0,0,0,512); grad.addColorStop(0,'#e4ff5c'); grad.addColorStop(.5,'#cdf52f'); grad.addColorStop(1,'#b6e21f');
    x.fillStyle=grad; x.fillRect(0,0,1024,512);
    for(let i=0;i<2600;i++){ x.fillStyle=`rgba(${Math.random()<.5?255:120},255,${Math.random()*60},${Math.random()*0.06})`; x.fillRect(Math.random()*1024,Math.random()*512,2,2); }
    x.strokeStyle='#fbfff0'; x.lineWidth=22; x.lineCap='round';
    for(let s=0;s<2;s++){ x.beginPath(); for(let i=0;i<=1024;i++){ const u=i/1024; const y=256+Math.sin(u*Math.PI*2+s*Math.PI)*150; i?x.lineTo(i,y):x.moveTo(i,y);} x.stroke(); }
    // brand print — stacked lockup like the official ball: slashes centered ABOVE the wordmark
    function ballMark(cx, cy, scale){
      const lh=64*scale;                       // slash mark height
      const lw=LOGO_W(lh);
      x.save();
      drawLogo(x, cx-lw/2, cy-lh-14*scale, lh, '#16240d');
      x.fillStyle='#16240d'; x.textAlign='center'; x.textBaseline='top';
      x.font='900 '+Math.round(52*scale)+'px Lato, sans-serif';
      x.fillText('Racket Up', cx, cy+2*scale);
      x.restore();
    }
    ballMark(300, 256, 1.0);
    ballMark(790, 256, 0.9);
    const tex=new THREE.CanvasTexture(c); tex.colorSpace=THREE.SRGBColorSpace; tex.anisotropy=4;
    const m=new THREE.Mesh(new THREE.SphereGeometry(46,64,64), new THREE.MeshStandardMaterial({map:tex,roughness:.92,metalness:0}));
    m.castShadow=true; m.receiveShadow=true; const g=new THREE.Group(); g.add(m); g.userData.spin=m; return g;
  }

  // draw the active design (real product photo) into the teardrop face region, cover-fit
  function paintDesign(i){
    const im=DESIGN_IMGS[i];
    if(!im || !im.complete || !im.naturalWidth) return;
    // fill to the racket edge: scale the artwork up slightly and re-centre.
    // Campbell Blair (3) prints smaller; the rest print larger.
    const ar=im.naturalWidth/im.naturalHeight;
    const sc = (i===3) ? 1.01 : 1.31;
    const dw=FACE.w*sc, dh=dw/ar;
    const dx=FACE.x-(dw-FACE.w)/2;
    const dy=FACE.y-(dh-FACE.w/ar)/2 + 22;  // ~1cm omlaag
    fctx.drawImage(im, dx, dy, dw, dh);
  }
  function redrawFace(a, b, t){
    const bg=hexLerp(DESIGNS[a].frame, DESIGNS[b].frame, t);
    if(!fctx) return bg;
    fctx.globalAlpha=1; fctx.fillStyle=bg; fctx.fillRect(0,0,fctx.canvas.width,fctx.canvas.height);
    if(t<1){ fctx.globalAlpha=1-t; paintDesign(a); }
    if(t>0){ fctx.globalAlpha=t;   paintDesign(b); }
    fctx.globalAlpha=1;
    faceTex.needsUpdate=true;
    return bg;
  }

  function applyDesign(i){
    if(i===curIdx) return;
    prevIdx=curIdx; curIdx=i; fadeT=0;
  }

  function processModel(obj){
    const rm=[]; obj.traverse(n=>{ if(n.isMesh){ const ms=Array.isArray(n.material)?n.material:[n.material];
      if(ms.some(m=>m&&/shadow/i.test(m.name))) rm.push(n);} });
    rm.forEach(n=>n.parent&&n.parent.remove(n));

    // opaque face-paint texture, planar-projected onto the REAL face geometry (no floating planes)
    const fc=document.createElement('canvas'); fc.width=720; fc.height=1160; fctx=fc.getContext('2d');
    faceTex=new THREE.CanvasTexture(fc); faceTex.colorSpace=THREE.SRGBColorSpace; faceTex.anisotropy=8;
    faceMat=new THREE.MeshPhysicalMaterial({ map:faceTex, roughness:.34, metalness:0, clearcoat:1, clearcoatRoughness:.09, envMapIntensity:1.2 });

    const faceMeshes=[];
    obj.traverse(n=>{ if(!n.isMesh) return; n.castShadow=true; n.receiveShadow=true;
      const nm=(n.name||'').toLowerCase();
      const om=(Array.isArray(n.material)?n.material:[n.material]).map(m=>(m&&m.name)||'').join(',').toLowerCase();
      let mat;
      if(/foam|hole/.test(nm)||/foamhole/.test(om)){ mat=new THREE.MeshStandardMaterial({color:0x0c0f08,roughness:.5,metalness:.2}); }
      else if(/rubber/.test(nm)){ mat=new THREE.MeshStandardMaterial({color:0x10180a,roughness:.82,metalness:.04}); gripMats.push(mat); }
      else if(/wristband|strap/.test(nm)){ mat=new THREE.MeshStandardMaterial({color:0x10180a,roughness:.8,metalness:.04}); bandMats.push(mat); }
      else if(/bottomlogo/.test(nm)||/bottomlogo/.test(om)){ mat=new THREE.MeshStandardMaterial({color:0xdbff01,roughness:.4,metalness:.1}); }
      else { mat=faceMat; faceMeshes.push(n); }
      n.material=mat;
    });

    obj.updateMatrixWorld(true);
    const box=new THREE.Box3().setFromObject(obj); const size=box.getSize(new THREE.Vector3()); const ctr=box.getCenter(new THREE.Vector3());
    obj.position.sub(ctr); obj.updateMatrixWorld(true);

    // planar projection (front view) -> per-vertex UVs so the paint wraps the actual surface
    const _v=new THREE.Vector3();
    let pMinX=1e9,pMaxX=-1e9,pMinY=1e9,pMaxY=-1e9;
    faceMeshes.forEach(m=>{ const p=m.geometry.attributes.position;
      for(let i=0;i<p.count;i++){ _v.set(p.getX(i),p.getY(i),p.getZ(i)).applyMatrix4(m.matrixWorld);
        if(_v.x<pMinX)pMinX=_v.x; if(_v.x>pMaxX)pMaxX=_v.x; if(_v.y<pMinY)pMinY=_v.y; if(_v.y>pMaxY)pMaxY=_v.y; } });
    const bw=pMaxX-pMinX, bh=pMaxY-pMinY;
    faceMeshes.forEach(m=>{ const p=m.geometry.attributes.position; const uv=new Float32Array(p.count*2);
      for(let i=0;i<p.count;i++){ _v.set(p.getX(i),p.getY(i),p.getZ(i)).applyMatrix4(m.matrixWorld);
        uv[i*2]=(_v.x-pMinX)/bw; uv[i*2+1]=(_v.y-pMinY)/bh; }
      m.geometry.setAttribute('uv', new THREE.BufferAttribute(uv,2)); });

    // size paint canvas to the projected box; teardrop fills the HEAD region (upper part), throat stays paint
    const CH=1160, CW=Math.max(360, Math.round(CH*bw/bh));
    fc.width=CW; fc.height=CH;
    FACE.x=0; FACE.y=0; FACE.w=CW; FACE.h=CH;

    const s=300/Math.max(size.x,size.y,size.z);
    const holder=new THREE.Group(); holder.scale.setScalar(s); holder.add(obj); racketRot.add(holder);
    ground.position.y=-(size.y*s)/2-18;

    // initial paint
    redrawFace(0,0,1);
    gripMats.forEach(m=>m.color.set(DESIGNS[0].grip));
    bandMats.forEach(m=>m.color.set(DESIGNS[0].band));
    DESIGN_IMGS.forEach(im=>{ if(im && !im.complete){ im.addEventListener('load',()=>{ redrawFace(prevIdx,curIdx,fadeT>=1?1:fadeT); }); } });
    window.__dbg={faceMeshes:faceMeshes.length, bw:+bw.toFixed(1), bh:+bh.toFixed(1), CW, CH};
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(()=>{ redrawFace(prevIdx,curIdx,fadeT>=1?1:fadeT); }); }

    ready=true;
    if(loaderEl) loaderEl.classList.add('done');
    document.documentElement.classList.add('scene-ready');
    // if the failsafe already revealed the static fallback (slow model load),
    // recover: drop scene-fail so the real 3D shows, and re-fit the canvas.
    document.documentElement.classList.remove('scene-fail');
    requestAnimationFrame(()=>{ try{ resize(); }catch(e){} });
  }

  // ---- load: Draco GLB when present, else FBX ----
  const FBX_URL = (window.__resources && window.__resources.racketFbx) || assetURL('assets/racket.fbx');
  const GLB_URL = (window.__resources && window.__resources.racketGlb) || assetURL('assets/racket.glb');
  function fail3D(e){ if(e) console.warn('3D model unavailable:', e.message||e); sceneFallback(); }
  const onProg = (p)=>{ if(barEl && p.total) barEl.style.width = Math.min(100,p.loaded/p.total*100)+'%'; };
  function loadFBX(){ new FBXLoader().load(FBX_URL, processModel, onProg, fail3D); }
  function loadGLB(){
    const draco=new DRACOLoader(); draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    const gltf=new GLTFLoader(); gltf.setDRACOLoader(draco);
    gltf.load(GLB_URL, (g)=>{ try{ processModel(g.scene); }catch(err){ console.warn(err); loadFBX(); } }, onProg, ()=>loadFBX());
  }
  if(window.__resources && window.__resources.racketGlb){ loadGLB(); }
  else { fetch(GLB_URL, { method:'HEAD' }).then(r => { if(r.ok) loadGLB(); else loadFBX(); }).catch(()=> loadFBX()); }

  // ---------- scroll-driven ----------
  const stt={ rotY:0,rotX:0,camZ:580,px:0,py:0,scl:1, bx:240,by:0,bz:80,bs:1,bo:0 };
  const cur={...stt};
  function wide(){ const r=canvas.getBoundingClientRect(); return r.width/Math.max(1,r.height)>1.1; }

  function sample(p,t){
    const offX=wide()?150:0;
    stt.rotY=key(p,[[0,0],[0.10,0],[0.30,-Math.PI*2],[0.50,-Math.PI*4],[0.70,-Math.PI*6],[0.90,-Math.PI*8],[1,-Math.PI*8-0.3]]);
    stt.rotX=key(p,[[0,0.03],[0.50,0.05],[0.90,0],[1,-0.10]]);
    stt.camZ=key(p,[[0,600],[0.10,560],[0.50,480],[0.90,520],[1,640]]);
    // racket alternates sides per beat (opposite the text): A right · B LEFT · C right · D LEFT · E center
    stt.px=offX*key(p,[[0,1],[0.16,1],[0.21,-1],[0.36,-1],[0.41,1],[0.56,1],[0.61,-1],[0.76,-1],[0.82,0.45],[1,0.55]]);
    const mob=!wide();
    const shortMob=mob && canvas.getBoundingClientRect().height<760;
    stt.py=mob ? (shortMob ? key(p,[[0,88],[0.12,88],[0.20,0],[0.5,0],[1,-8]]) : key(p,[[0,112],[0.12,112],[0.20,0],[0.5,0],[1,-8]])) : key(p,[[0,-4],[0.5,0],[1,-8]]);
    stt.scl=mob ? (shortMob ? key(p,[[0,0.30],[0.12,0.30],[0.20,0.62],[0.5,0.648],[1,0.62]]) : key(p,[[0,0.38],[0.12,0.38],[0.20,0.7],[0.5,0.728],[1,0.7]])) : key(p,[[0,0.7],[0.5,0.728],[1,0.7]]);
    let bx,by,bz,bs;
    if(p<0.12){ const a=t*0.6; bx=210+Math.cos(a)*120; by=30+Math.sin(a)*70; bz=Math.sin(a)*120+80; bs=0.85; }
    else { bx=-280; by=180; bz=-160; bs=0.55; }
    stt.bx=bx; stt.by=by; stt.bz=bz; stt.bs=bs;
    stt.bo=key(p,[[0,0],[0.05,1],[0.74,1],[0.82,0]]);
    applyDesign(designIndexAt(p));
  }

  const clock=new THREE.Clock();
  function frame(){
    requestAnimationFrame(frame);
    if(!sceneVisible) return;
    const dt=clock.getDelta(), t=clock.elapsedTime; sample(targetP,t); const k=0.085;
    cur.rotY=lerp(cur.rotY,stt.rotY,k); cur.rotX=lerp(cur.rotX,stt.rotX,k);
    cur.camZ=lerp(cur.camZ,stt.camZ,k); cur.px=lerp(cur.px,stt.px,k); cur.py=lerp(cur.py,stt.py,k); cur.scl=lerp(cur.scl,stt.scl,k);
    cur.bx=lerp(cur.bx,stt.bx,k); cur.by=lerp(cur.by,stt.by,k); cur.bz=lerp(cur.bz,stt.bz,k); cur.bs=lerp(cur.bs,stt.bs,.12); cur.bo=lerp(cur.bo,stt.bo,.12);
    racketRot.rotation.set(cur.rotX,cur.rotY,0);
    racketPos.position.set(cur.px,cur.py+Math.sin(t*0.9)*4,0);
    racketPos.scale.setScalar(cur.scl);
    camera.position.z=cur.camZ;
    ball.position.set(cur.bx,cur.by,cur.bz); ball.scale.setScalar(cur.bs);
    if(ball.userData.spin){ ball.userData.spin.rotation.y+=.03; ball.userData.spin.rotation.x+=.012;
      ball.userData.spin.material.opacity=cur.bo; ball.userData.spin.material.transparent=cur.bo<.99; }
    // design cross-fade baked into the surface texture
    if(ready && fadeT<1){
      fadeT=Math.min(1, fadeT + dt/0.45);
      const e=fadeT*fadeT*(3-2*fadeT);
      const bg=redrawFace(prevIdx,curIdx,e);
      frameMats.forEach(m=>m.color.set(bg));
      const gc=hexLerp(DESIGNS[prevIdx].grip, DESIGNS[curIdx].grip, e);
      const bc=hexLerp(DESIGNS[prevIdx].band, DESIGNS[curIdx].band, e);
      gripMats.forEach(m=>m.color.set(gc));
      bandMats.forEach(m=>m.color.set(bc));
    }
    renderer.render(scene,camera);
  }
  frame();

  function resize(){ const r=canvas.getBoundingClientRect(); const w=Math.max(1,r.width),h=Math.max(1,r.height);
    renderer.setSize(w,h,false); camera.aspect=w/h; camera.updateProjectionMatrix(); }
  new ResizeObserver(resize).observe(canvas); addEventListener('resize',resize); resize();

  // ---- public API ----
  window.boostScene = {
    setProgress:(p)=>{ targetP=clamp(p,0,1); },
    setName:(txt)=>{ state.name=txt||''; if(ready) redrawFace(prevIdx,curIdx,fadeT>=1?1:fadeT); },
    designCount: DESIGNS.length,
    get ready(){ return ready; }
  };
}
